// A2-484 — answer "is this rent for the property itself?".
//
// PROPERTY_LETTING_UNANSWERED: a property service in Germany, Austria or
// Spain was treated as one where the customer pays the VAT, because the
// client is not based there. That is right for building work, agents or
// management, and wrong for RENT — in those countries the owner declares
// rent on its own return. The app has one "property service" type, so it
// has to ask. BLOCKING: the answer changes which box the figure lands in.
//
// Modelled on CustomerEstablishmentResolver (A2-209): pick an answer, see
// its consequence, pick a reason ("pick, don't write"), save; the next re-run applies it.
// BULK: monthly rent is the same answer every month, so several ticked
// rows can be answered at once; the backend keeps one record per row.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolvePropertyLetting } from "../../lib/api";
import { countryName } from "../../lib/countries";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { LETTING_REASONS } from "../../lib/reviewReasonPresets";
import { STICKY_BAR } from "./FamilyBBulkResolver";

// The scale's own step (see CustomerEstablishmentResolver for why no
// per-pixel sizes).
const SZ = "text-xs";

// Kept in step with backend/engine/art194_election.py LETTING / NOT_LETTING
// (the route refuses anything else). A jest test pins the values.
export const LETTING_ANSWERS = [
  {
    value: "LETTING",
    key: "let_answer_yes",
    label: "Yes — rent for the property (land, building, office, flat, rooms, parking)",
    outcomeKey: "let_outcome_yes",
    outcome: "This rent now goes on your client's {{country}} return at the VAT the invoice shows. If it shows none, you will be asked whether the rent is exempt or which rate applies. Your client needs a {{country}} VAT registration for it.",
  },
  {
    value: "NOT_LETTING",
    key: "let_answer_no",
    label: "No — another service for the property",
    outcomeKey: "let_outcome_no",
    outcome: "No change: the customer pays the VAT, as now.",
  },
];

const rowsLabel = (t, ids) => (ids.length > 1
  ? t("ui.review_grouped.n_rows", { n: ids.length, defaultValue: `${ids.length} rows` })
  : `${ids[0].slice(0, 12)}…`);

export const PropertyLettingResolver = ({
  tids, calcId, calcFrozen, acked, country, onResolved, hideChip = false,
  sel, setSel,
  // A2-484 review — the row's recorded answer (LETTING / NOT_LETTING): shown,
  // and the button offers to change it, on the PROPERTY_LETTING_ANSWERED card.
  current,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const ids = Array.isArray(tids) ? tids : [tids];
  const many = ids.length > 1;
  const key = many ? `grp-${ids.length}` : ids[0];
  const place = countryName(country) || t("ui.review_grouped.let_country_fallback",
    { defaultValue: "that country" });
  // i18next interpolates {{country}} itself; this also covers a t() that
  // does not (and the default values below).
  const fill = (s) => String(s).split("{{country}}").join(place);

  const reset = () => { setAnswer(""); setReason(""); setPreset(""); setErr(null); };
  const cleaned = reason.trim();
  const valid = !!answer && cleaned.length >= 8 && !/[<>]/.test(cleaned);
  const picked = LETTING_ANSWERS.find((a) => a.value === answer);
  const currentPick = LETTING_ANSWERS.find((a) => a.value === current);
  const labelOf = (a) => t(`ui.review_grouped.${a.key}`, { defaultValue: a.label });

  if (acked) {
    return (
      <>
        {!hideChip && (
          <span data-testid={`rg-txn-${key}`} data-resolved="true"
            className={`px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate tabular-nums ${SZ}`}>
            ✓ {rowsLabel(t, ids)}
          </span>
        )}
      </>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await resolvePropertyLetting(calcId, { txn_ids: ids, answer, reason: cleaned });
      setOpen(false); reset();
      toast.success(
        t("ui.review_grouped.est_toast_ok", { defaultValue: "Saved — figures change at the next re-run" }),
        { description: `${rowsLabel(t, ids)} → ${picked ? labelOf(picked) : answer}` },
      );
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(t("ui.review_grouped.est_toast_fail", { defaultValue: "Could not save" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {setSel && !many && !calcFrozen && (
          <input
            type="checkbox"
            data-testid={`letting-check-${key}`}
            checked={(sel || []).includes(ids[0])}
            onChange={(e) => setSel((prev) => (e.target.checked
              ? [...prev, ids[0]] : prev.filter((x) => x !== ids[0])))}
            aria-label={t("ui.review_grouped.let_bulk_check_aria", {
              defaultValue: "Select this row to answer several at once",
            })}
            className="accent-emerald-600"
          />
        )}
        {!hideChip && (
          <span data-testid={`rg-txn-${key}`} data-resolved="false"
            className={`px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate tabular-nums ${SZ}`}>
            {rowsLabel(t, ids)}
          </span>
        )}
        {!calcFrozen && (
          <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={busy}
                data-testid={`letting-resolve-open-${key}`}
                data-busy={busy ? "true" : undefined}
                className={`tabular-nums px-2 py-1 rounded border ${busy
                  ? "border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 cursor-wait"
                  : "border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200"} ${SZ}`}
              >
                {busy
                  ? t("ui.review_grouped.est_submitting", { defaultValue: "Saving…" })
                  : many
                    ? t("ui.review_grouped.let_resolve_btn_bulk", {
                      defaultValue: "Answer for {{count}} rows →", count: ids.length,
                    })
                    : current
                      ? t("ui.review_grouped.let_change_btn", { defaultValue: "Change the answer →" })
                      : t("ui.review_grouped.let_resolve_btn", { defaultValue: "Is this rent? →" })}
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid={`letting-resolve-popover-${key}`}
              className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
            >
              <div className={`text-slate-700 dark:text-slate-200 font-bold ${SZ}`}>
                {t("ui.review_grouped.let_resolve_title", {
                  defaultValue: "Is this rent for the property itself?",
                })}
              </div>
              <div data-testid={`letting-resolve-help-${key}`}
                className={`text-slate-500 dark:text-slate-400 leading-snug ${SZ}`}>
                {fill(t("ui.review_grouped.let_resolve_help", {
                  country: place,
                  defaultValue:
                    "Your client is not based in {{country}}, so this property service was treated as one where the customer pays the VAT. That is right for building work, agents, surveyors, cleaning or management. It is not right for rent: in {{country}} the owner declares rent on its own {{country}} return. Rent is often exempt from VAT; where it is taxed (the owner chose to charge VAT, a short stay, parking, most business rent in Spain), the VAT is due from the owner.",
                }))}
              </div>
              {currentPick && (
                <div data-testid={`letting-resolve-current-${key}`}
                  className={`rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ${SZ}`}>
                  {t("ui.review_grouped.let_current", { defaultValue: "Current answer" })}: {labelOf(currentPick)}
                </div>
              )}
              {many && (
                <div data-testid={`letting-resolve-bulk-note-${key}`}
                  className={`rounded px-2 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 ${SZ}`}>
                  {t("ui.review_grouped.let_resolve_bulk_note", {
                    defaultValue: "Answers all {{count}} selected rows. Each keeps its own audit record.",
                    count: ids.length,
                  })}
                </div>
              )}
              <select
                data-testid={`letting-resolve-answer-${key}`}
                value={answer}
                onChange={(e) => { setAnswer(e.target.value); setPreset(""); setReason(""); }}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
              >
                <option value="">
                  {t("ui.review_grouped.est_answer_placeholder", { defaultValue: "Select…" })}
                </option>
                {LETTING_ANSWERS.map((a) => (
                  <option key={a.value} value={a.value}>{labelOf(a)}</option>
                ))}
              </select>
              {picked && (
                <div data-testid={`letting-resolve-outcome-${key}`}
                  className={`rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ${SZ}`}>
                  {fill(t(`ui.review_grouped.${picked.outcomeKey}`,
                    { country: place, defaultValue: picked.outcome }))}
                </div>
              )}
              {answer && (
                <PresetReasonSelect
                  testid={`letting-resolve-reason-preset-${key}`}
                  presets={LETTING_REASONS[answer] || []}
                  preset={preset}
                  onPick={(p, v) => { setPreset(p); setReason(v); }}
                />
              )}
              {err && (
                <div data-testid={`letting-resolve-error-${key}`} className={`text-red-700 dark:text-red-300 ${SZ}`}>
                  {err}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`letting-resolve-cancel-${key}`}
                  onClick={() => { setOpen(false); reset(); }}
                  className={`underline text-slate-600 dark:text-slate-300 ${SZ}`}
                >
                  {t("ui.review_grouped.est_cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  data-testid={`letting-resolve-submit-${key}`}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/resolve-property-letting"
                  disabled={busy || !valid}
                  onClick={submit}
                  className={`font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white ${SZ}`}
                >
                  {busy
                    ? t("ui.review_grouped.est_submitting", { defaultValue: "Saving…" })
                    : t("ui.review_grouped.est_submit", { defaultValue: "Save" })}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
};

// The group-level bulk answer: rows are ticked on their own lines (the
// checkbox PropertyLettingResolver draws when given `setSel`), nothing is
// pre-selected, and the button carries the count.
export const PropertyLettingBulk = ({
  g, sel, setSel, calcId, calcFrozen, onResolved,
}) => {
  const { t } = useTranslation();
  const acked = g?.acknowledged_txn_ids || [];
  const ids = (g?.txn_ids || []).filter((x) => !acked.includes(x));
  if (calcFrozen || ids.length < 2) return null;
  const chosen = (sel || []).filter((x) => ids.includes(x));
  // Name the country only when every ticked row is in the same one.
  const countryOf = (x) => (g.rows || []).find((r) => r.txn_id === x)?.rc_rule?.country;
  const places = new Set(chosen.map(countryOf).filter(Boolean));
  const country = places.size === 1 ? [...places][0] : undefined;
  return (
    <div data-testid="letting-bulk" className={`${STICKY_BAR} flex items-center gap-2 flex-wrap mb-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2`}>
      <span className="text-slate-600 dark:text-slate-300">
        {t("ui.review_grouped.let_bulk_hint", {
          defaultValue: "Same kind of invoice on several rows? Tick them and answer once.",
        })}
      </span>
      <button type="button" data-testid="letting-bulk-selectall" onClick={() => setSel(ids)}
        className="underline text-slate-600 dark:text-slate-300">
        {t("ui.review_grouped.est_bulk_all", { defaultValue: "Select all {{count}}", count: ids.length })}
      </button>
      {chosen.length > 0 && (
        <button type="button" data-testid="letting-bulk-clear" onClick={() => setSel([])}
          className="underline text-slate-600 dark:text-slate-300">
          {t("ui.review_grouped.est_bulk_clear", { defaultValue: "Clear" })}
        </button>
      )}
      {chosen.length > 1 && (
        <PropertyLettingResolver tids={chosen} calcId={calcId} calcFrozen={calcFrozen}
          acked={false} country={country} onResolved={onResolved} />
      )}
    </div>
  );
};

export default PropertyLettingResolver;
